import React from 'react';
import { matchPath, NavLink } from 'react-router-dom';
import { Box, Divider, ListItemButton, Tooltip, Typography } from '@mui/material';
import {
  Building2,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChartColumn,
  ClipboardCheck,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Mail,
  Network,
  Settings,
  Upload,
  UserCheck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { AuthRole, MANAGER_LIKE_ROLES } from '../../utils/roles';
import { sbg, transition, typeStyle } from '../../theme/sbgTokens';

interface SidebarProps {
  onItemClick?: () => void;
  collapsed?: boolean;
}

export interface NavigationItem {
  text: string;
  icon: LucideIcon;
  path: string;
  exact?: boolean;
  roles: readonly AuthRole[];
  /** Extra words the command palette matches ("pto", "desks"). */
  keywords?: readonly string[];
}

export interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

// Group destinations by the user's work, while retaining their existing URLs
// and access rules. This also supplies the top bar's location context and the
// command palette's Pages group.
const navigationSections: NavigationSection[] = [
  {
    title: 'Workspace',
    items: [
      { text: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', roles: ['admin', 'hr', ...MANAGER_LIKE_ROLES], keywords: ['home', 'overview', 'today'] },
      { text: 'Employees', icon: Users, path: '/employees', roles: ['admin', 'hr'], keywords: ['people', 'staff', 'directory'] },
      { text: 'Departments', icon: Building2, path: '/departments', roles: ['admin', 'hr'], keywords: ['teams', 'groups'] },
      { text: 'Org chart', icon: Network, path: '/org-chart', roles: ['admin', 'hr', ...MANAGER_LIKE_ROLES, 'employee'], keywords: ['reporting', 'managers', 'hierarchy'] },
      { text: 'Floor plan', icon: LayoutGrid, path: '/admin/floorplan', roles: ['admin', ...MANAGER_LIKE_ROLES, 'employee'], keywords: ['seats', 'desks', 'office map'] },
    ],
  },
  {
    title: 'Attendance',
    items: [
      { text: 'Time off', icon: CalendarRange, path: '/time-off', roles: ['admin', 'hr', ...MANAGER_LIKE_ROLES], keywords: ['pto', 'sick', 'wfh', 'leave', 'requests'] },
      { text: 'HR reviews', icon: ClipboardCheck, path: '/hr-time-off', exact: true, roles: ['admin', 'hr'], keywords: ['approvals', 'pending', 'requests'] },
      { text: 'Calendar view', icon: CalendarDays, path: '/hr-time-off/calendar', exact: true, roles: [...MANAGER_LIKE_ROLES], keywords: ['calendar', 'schedule', 'who is out'] },
      // Read-only, server-scoped team view; admins keep the full admin pages.
      { text: 'Team attendance', icon: ListChecks, path: '/team-attendance', roles: [...MANAGER_LIKE_ROLES], keywords: ['my team', 'attendance'] },
      { text: 'Weekend shifts', icon: CalendarClock, path: '/admin/weekend-shifts', roles: ['admin'], keywords: ['saturday', 'sunday', 'coverage'] },
      { text: 'Office check-in', icon: UserCheck, path: '/admin/sales-office-checkin', roles: ['admin'], keywords: ['sales office', 'check in', 'arrivals'] },
    ],
  },
  {
    title: 'Insights',
    items: [
      { text: 'Analytics', icon: ChartColumn, path: '/analytics', roles: ['admin', 'hr'], keywords: ['reports', 'trends', 'usage'] },
      { text: 'Email activity', icon: Mail, path: '/admin/emails', roles: ['admin', 'hr'], keywords: ['emails', 'sent', 'messages'] },
    ],
  },
];

const TOOLS_TITLE = 'Tools';

const utilityItems: NavigationItem[] = [
  { text: 'Import data', icon: Upload, path: '/import', roles: ['admin', 'hr'], keywords: ['upload', 'csv', 'bulk'] },
  { text: 'Settings', icon: Settings, path: '/admin', exact: true, roles: ['admin', 'hr'], keywords: ['admin', 'users', 'roles', 'access', 'calendar sync'] },
];

export interface NavigationContext {
  /** The sidebar group ("Workspace"); a nav grouping, not a hierarchy level. */
  section: string;
  /** The matching destination's name ("Employees"). */
  title: string;
  /** The matching destination, when there is one. */
  item?: NavigationItem;
  /** True on a record under the destination (/employees/:id), not the destination itself. */
  nested: boolean;
}

export const getNavigationContext = (pathname: string): NavigationContext => {
  for (const section of [...navigationSections, { title: TOOLS_TITLE, items: utilityItems }]) {
    const item = section.items.find(({ path, exact }) => matchPath({ path, end: exact ?? false }, pathname));
    if (item) {
      return { section: section.title, title: item.text, item, nested: !matchPath({ path: item.path, end: true }, pathname) };
    }
  }
  return { section: 'Workspace', title: 'Attendance', nested: false };
};

/** A role's destinations, grouped and ordered exactly as the sidebar shows them. */
export const getNavigationSections = (role: AuthRole) => ({
  sections: navigationSections
    .map((section) => ({ ...section, items: section.items.filter((item) => item.roles.includes(role)) }))
    .filter((section) => section.items.length > 0),
  tools: { title: TOOLS_TITLE, items: utilityItems.filter((item) => item.roles.includes(role)) },
});

/** Labels fade rather than unmount, so nothing jumps while the rail animates. */
const fade = (hidden: boolean) => ({ opacity: hidden ? 0 : 1, transition: transition('opacity') });

/*
 * Sidebar item (book §11.7): a 36px pill (44px touch target in the mobile
 * drawer, §11.1), 12px padding, 10px icon-label gap, 18px icon, label-md.
 * Current: selected wash, primary label, accent icon and aria-current="page"
 * (NavLink sets both together). States change color only. Padding stays the
 * same when collapsed: 12px nav + 12px item padding puts the 18px icon at
 * 24–42px, centered in the 64px rail, so collapsing never moves an icon.
 */
const NavItem: React.FC<{ item: NavigationItem; collapsed: boolean; onClick?: () => void }> = ({ item, collapsed, onClick }) => {
  const Icon = item.icon;
  // Always wrapped (an empty title never shows) so collapsing doesn't remount the link.
  return (
    <Tooltip title={collapsed ? item.text : ''} placement="right">
      <ListItemButton
        component={NavLink}
        to={item.path}
        end={item.exact}
        aria-label={item.text}
        onClick={onClick}
        sx={{
          flex: 'none',
          gap: '10px',
          height: { xs: 44, md: 36 },
          minHeight: { xs: 44, md: 36 },
          px: 1.5,
          py: 0,
          justifyContent: 'flex-start',
          overflow: 'hidden',
          borderRadius: sbg.radius.pill,
          color: sbg.text.secondary,
          ...typeStyle('label-md'),
          transition: transition('background-color', 'color'),
          '& .sbg-nav-icon': { flex: 'none', color: 'inherit', transition: transition('color') },
          '&:hover': { bgcolor: sbg.bg.hover, color: sbg.text.primary },
          '&.active': {
            bgcolor: sbg.bg.selected,
            color: sbg.text.primary,
            '& .sbg-nav-icon': { color: sbg.text.accent },
            '&:hover': { bgcolor: sbg.bg.selectedStrong },
          },
        }}
      >
        <Icon className="sbg-nav-icon" size={18} aria-hidden="true" />
        <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...fade(collapsed) }}>
          {item.text}
        </Box>
      </ListItemButton>
    </Tooltip>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ onItemClick, collapsed = false }) => {
  const { profile } = useAuth();
  const role = (profile?.role || 'employee') as AuthRole;
  const { sections, tools } = getNavigationSections(role);

  const renderItems = (items: NavigationItem[]) => (
    <Box sx={{ display: 'grid', gap: '2px' }}>
      {items.map((item) => (
        <NavItem key={item.path} item={item} collapsed={collapsed} onClick={onItemClick} />
      ))}
    </Box>
  );

  return (
    <Box
      component="nav"
      id="primary-navigation"
      aria-label="Primary navigation"
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        px: 1.5,
        pt: 2,
        pb: 1,
      }}
    >
      {sections.map((section, index) => (
        <Box component="section" aria-label={section.title} key={section.title}>
          {/* The overline row keeps its height when collapsed; a hairline replaces the label between groups. */}
          <Typography
            variant="overline"
            component="h2"
            sx={{
              position: 'relative',
              display: 'block',
              px: 1.5,
              pb: 0.75,
              color: sbg.text.tertiary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 8,
                right: 8,
                top: 8,
                height: '1px',
                bgcolor: sbg.border.default,
                ...fade(!(collapsed && index > 0)),
              },
            }}
          >
            <Box component="span" sx={{ display: 'block', ...fade(collapsed) }}>
              {section.title}
            </Box>
          </Typography>
          {renderItems(section.items)}
        </Box>
      ))}
      {tools.items.length > 0 && (
        <Box component="section" aria-label={tools.title} sx={{ mt: 'auto' }}>
          <Divider sx={{ mx: 1.5, mb: 1.5 }} />
          {renderItems(tools.items)}
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
